// src/pages/AEPReadiness.jsx
// AEP Readiness Command Center — agency-owner view
// Tabbed: Command Center | Coverage Reviews | Agent Ops | Carriers | Post-AEP

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import {
  Trophy, Target, Users, TrendingUp, AlertCircle, CheckCircle2,
  Clock, Star, Shield, Award, Zap, ChevronDown, ChevronUp,
  RefreshCw, Plus, Edit2, Save, X, ArrowRight, Calendar,
  FileText, DollarSign, BarChart2, Activity, Flag, Crown,
  Medal, Flame, Check
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ============================================================================
// AEP date helpers (client-side mirror of backend logic)
// ============================================================================
const AEP_TOTAL_DAYS = 54;

function getAepDates(year) {
  return {
    start: new Date(year, 9, 15, 0, 0, 0),    // Oct 15
    end:   new Date(year, 11, 7, 23, 59, 59),  // Dec 7
  };
}

function getAepPhaseClient(now = new Date()) {
  const year = now.getFullYear();
  const { start, end } = getAepDates(year);
  if (now < start) return { phase: 'pre', year, start, end };
  if (now <= end)  return { phase: 'active', year, start, end };
  return { phase: 'post', year, start, end };
}

function msToCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(totalSeconds / (60 * 60 * 24));
  const h = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const m = Math.floor((totalSeconds % (60 * 60)) / 60);
  const s = totalSeconds % 60;
  return { d, h, m, s };
}

// ============================================================================
// Utility helpers
// ============================================================================
function fmt(n) {
  return Number(n || 0).toLocaleString();
}
function fmtPct(n) {
  return `${Math.round(n || 0)}%`;
}
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function pad2(n) {
  return String(n).padStart(2, '0');
}

// ============================================================================
// Design-system primitives
// ============================================================================
function Card({ children, className = '' }) {
  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

function ProgressBar({ value, max, color = 'bg-amber-400', label, showPct = true }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        {label && <span className="text-xs text-gray-400">{label}</span>}
        {showPct && <span className="text-xs tabular-nums text-gray-300">{pct}%</span>}
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Badge({ children, color = 'amber' }) {
  const colors = {
    amber:   'bg-amber-400/10 text-amber-400 border border-amber-400/20',
    emerald: 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20',
    red:     'bg-red-400/10 text-red-400 border border-red-400/20',
    blue:    'bg-blue-400/10 text-blue-400 border border-blue-400/20',
    gray:    'bg-gray-700/50 text-gray-300 border border-gray-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    not_contacted: { label: 'Not Contacted', color: 'gray' },
    pending:       { label: 'Pending',        color: 'amber' },
    scheduled:     { label: 'Scheduled',      color: 'blue' },
    reviewed:      { label: 'Reviewed',        color: 'emerald' },
    overdue:       { label: 'Overdue',         color: 'red' },
  };
  const { label, color } = map[status] || { label: status, color: 'gray' };
  return <Badge color={color}>{label}</Badge>;
}

function PriorityBadge({ priority }) {
  const map = {
    CRITICAL: 'red',
    HIGH:     'amber',
    STANDARD: 'gray',
  };
  return <Badge color={map[priority] || 'gray'}>{priority}</Badge>;
}

// ============================================================================
// AEP STATUS BANNER
// ============================================================================
function AEPStatusBanner({ year }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const { phase, start, end } = getAepPhaseClient(now);

  // Determine banner color
  let bannerClass = 'border-amber-400/40 bg-amber-400/5';
  let dotClass    = 'bg-amber-400';
  let phaseLabel  = '';

  if (phase === 'active') {
    bannerClass = 'border-emerald-400/40 bg-emerald-400/5';
    dotClass    = 'bg-emerald-400';
    phaseLabel  = 'AEP ACTIVE';
  } else if (phase === 'post') {
    bannerClass = 'border-gray-600/40 bg-gray-800/30';
    dotClass    = 'bg-gray-500';
    phaseLabel  = 'POST-AEP';
  } else {
    phaseLabel = 'PRE-AEP';
  }

  let centerContent = null;
  if (phase === 'pre') {
    const ms  = start - now;
    const { d, h, m, s } = msToCountdown(ms);
    const urgencyClass = d <= 7 ? 'text-red-400' : d <= 30 ? 'text-amber-400' : 'text-gray-200';
    centerContent = (
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400">AEP {year} starts in</span>
        <div className={`flex items-center gap-1 font-mono tabular-nums font-bold ${urgencyClass}`}>
          <span className="text-lg">{pad2(d)}d</span>
          <span className="text-gray-600 mx-0.5">:</span>
          <span className="text-lg">{pad2(h)}h</span>
          <span className="text-gray-600 mx-0.5">:</span>
          <span className="text-lg">{pad2(m)}m</span>
          <span className="text-gray-600 mx-0.5">:</span>
          <span className="text-lg">{pad2(s)}s</span>
        </div>
        <span className="text-sm text-gray-500">(Oct 15 – Dec 7)</span>
      </div>
    );
  } else if (phase === 'active') {
    const dayNum      = Math.max(1, Math.min(AEP_TOTAL_DAYS, Math.ceil((now - start) / 86400000)));
    const msLeft      = end - now;
    const { d, h, m } = msToCountdown(msLeft);
    centerContent = (
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-emerald-400 font-bold text-lg tabular-nums">Day {dayNum}</span>
          <span className="text-gray-500 text-sm">of {AEP_TOTAL_DAYS}</span>
        </div>
        <div className="h-4 w-px bg-gray-700" />
        <div className="text-sm text-gray-400 tabular-nums">
          {pad2(d)}d {pad2(h)}h {pad2(m)}m remaining
        </div>
        <div className="h-24 w-24 relative">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#1f2937" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15" fill="none"
              stroke="#34d399" strokeWidth="3"
              strokeDasharray={`${(dayNum / AEP_TOTAL_DAYS) * 94.2} 94.2`}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-emerald-400 tabular-nums">
            {Math.round((dayNum / AEP_TOTAL_DAYS) * 100)}%
          </span>
        </div>
      </div>
    );
  } else {
    centerContent = (
      <span className="text-sm text-gray-400">
        AEP {year} ended Dec 7 — review retrospective in Post-AEP tab
      </span>
    );
  }

  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${bannerClass} mb-6`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dotClass} ${phase === 'active' ? 'animate-pulse' : ''}`} />
        <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">{phaseLabel}</span>
      </div>
      {centerContent}
      <span className="text-xs text-gray-500 tabular-nums">AEP {year}</span>
    </div>
  );
}

// ============================================================================
// TAB 1: COMMAND CENTER
// ============================================================================
function KPICard({ title, value, sub, icon: Icon, color = 'text-amber-400', trend }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wider">{title}</p>
          <p className={`text-3xl font-bold tabular-nums mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className="p-2 bg-gray-800 rounded-lg">
          <Icon size={20} className={color} />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-3">
          <ProgressBar value={trend} max={100} color={color === 'text-emerald-400' ? 'bg-emerald-400' : 'bg-amber-400'} showPct={false} />
        </div>
      )}
    </Card>
  );
}

function VelocityChart({ data, goal }) {
  const dailyTarget = goal > 0 ? Math.ceil(goal / AEP_TOTAL_DAYS) : 0;

  const chartData = data.map(d => ({
    ...d,
    target: dailyTarget,
    label: d.date ? d.date.slice(5) : '',
  }));

  return (
    <ResponsiveContainer width="100%" height={180}>
      <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="velGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, color: '#fff' }}
          labelStyle={{ color: '#9ca3af', fontSize: 11 }}
          formatter={(v, name) => [v, name === 'enrollments' ? 'Enrollments' : 'Daily Target']}
        />
        <Area
          type="monotone" dataKey="enrollments"
          stroke="#f59e0b" strokeWidth={2}
          fill="url(#velGrad)" dot={false}
        />
        {dailyTarget > 0 && (
          <Area
            type="monotone" dataKey="target"
            stroke="#374151" strokeWidth={1} strokeDasharray="4 3"
            fill="none" dot={false}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DailyBriefingCard({ briefing }) {
  if (!briefing) return null;
  const { wins = [], priorities = [], risks = [] } = briefing;

  return (
    <Card className="p-4">
      <SectionHeader title="Daily Briefing" subtitle="Rule-based intel — updated on load" />
      <div className="space-y-4">
        {wins.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <CheckCircle2 size={12} /> Wins
            </p>
            <ul className="space-y-1">
              {wins.map((w, i) => (
                <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5 shrink-0">+</span> {w}
                </li>
              ))}
            </ul>
          </div>
        )}
        {priorities.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Flag size={12} /> Priorities
            </p>
            <ul className="space-y-1">
              {priorities.map((p, i) => (
                <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5 shrink-0">!</span> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
        {risks.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <AlertCircle size={12} /> Risks
            </p>
            <ul className="space-y-1">
              {risks.map((r, i) => (
                <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                  <span className="text-red-400 mt-0.5 shrink-0">▲</span> {r}
                </li>
              ))}
            </ul>
          </div>
        )}
        {wins.length === 0 && priorities.length === 0 && risks.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No briefing items. Add enrollments and coverage reviews to generate insights.</p>
        )}
      </div>
    </Card>
  );
}

function LeaderboardWidget({ leaderboard }) {
  const medals = [
    <Crown key="1" size={16} className="text-amber-400" />,
    <Medal key="2" size={16} className="text-gray-400" />,
    <Medal key="3" size={16} className="text-amber-700" />,
  ];
  const max = leaderboard[0]?.enrollments || 1;

  return (
    <Card className="p-4">
      <SectionHeader title="Enrollment Leaderboard" subtitle="This AEP season" />
      {leaderboard.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No enrollments logged yet.</p>
      ) : (
        <div className="space-y-3">
          {leaderboard.map((a, i) => (
            <div key={a.agentId} className="flex items-center gap-3">
              <div className="w-5 text-center shrink-0">
                {medals[i] || <span className="text-xs text-gray-500">#{a.rank}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white truncate">{a.agentName}</span>
                  <span className="text-sm font-bold tabular-nums text-amber-400 ml-2">{fmt(a.enrollments)}</span>
                </div>
                <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400/60 rounded-full transition-all duration-500"
                    style={{ width: `${Math.round((a.enrollments / max) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CommandCenterTab({ data, year, onRefresh, refreshing }) {
  const { kpis, velocityData, briefing, leaderboard, coverageSummary, agentSummary } = data;

  const momentumColor = kpis.momentumScore >= 80 ? 'text-emerald-400'
    : kpis.momentumScore >= 50 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Enrollments"
          value={fmt(kpis.totalEnrollments)}
          sub={`Goal: ${fmt(kpis.enrollmentGoal)} (${fmtPct(kpis.enrollmentPct)})`}
          icon={Target}
          color="text-amber-400"
          trend={kpis.enrollmentPct}
        />
        <KPICard
          title="Coverage Reviews"
          value={fmtPct(kpis.coverageReviewPct)}
          sub={`${fmt(coverageSummary.reviewed)} of ${fmt(coverageSummary.total)} clients`}
          icon={CheckCircle2}
          color="text-emerald-400"
          trend={kpis.coverageReviewPct}
        />
        <KPICard
          title="Agent Utilization"
          value={fmtPct(kpis.agentUtilization)}
          sub={`${fmt(agentSummary.certified)}/${fmt(agentSummary.total)} AHIP certified`}
          icon={Users}
          color="text-blue-400"
          trend={kpis.agentUtilization}
        />
        <KPICard
          title="Momentum Score"
          value={kpis.momentumScore}
          sub="0–100 composite pace score"
          icon={Zap}
          color={momentumColor}
          trend={kpis.momentumScore}
        />
      </div>

      {/* Velocity chart + briefing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-4">
          <SectionHeader title="Enrollment Velocity" subtitle="Last 14 days vs. daily target" />
          <VelocityChart data={velocityData || []} goal={kpis.enrollmentGoal} />
        </Card>
        <DailyBriefingCard briefing={briefing} />
      </div>

      {/* Leaderboard + coverage snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LeaderboardWidget leaderboard={leaderboard || []} />
        <Card className="p-4">
          <SectionHeader title="Coverage Review Snapshot" />
          <div className="space-y-3">
            {[
              { label: 'Reviewed',      value: coverageSummary.reviewed,      color: 'bg-emerald-400' },
              { label: 'Scheduled',     value: coverageSummary.scheduled,     color: 'bg-blue-400' },
              { label: 'Overdue',       value: coverageSummary.overdue,       color: 'bg-red-400' },
              { label: 'Not Contacted', value: coverageSummary.total - coverageSummary.reviewed - coverageSummary.scheduled - coverageSummary.overdue, color: 'bg-gray-600' },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="tabular-nums text-gray-200">{fmt(item.value)}</span>
                </div>
                <ProgressBar
                  value={Math.max(0, item.value)}
                  max={Math.max(1, coverageSummary.total)}
                  color={item.color}
                  showPct={false}
                />
              </div>
            ))}
          </div>
          {coverageSummary.soaMissing > 0 && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
              <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
              <p className="text-xs text-red-300">
                <span className="font-semibold">{coverageSummary.soaMissing} appointment(s)</span> in the next 48h are missing SOA documentation.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// TAB 2: COVERAGE REVIEWS
// ============================================================================
function ReviewRow({ review, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status:           review.status,
    priority:         review.priority,
    soa_obtained:     review.soa_obtained,
    appointment_date: review.appointment_date ? review.appointment_date.slice(0, 16) : '',
    notes:            review.notes || '',
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    await onUpdate(review.id, form);
    setEditing(false);
    setSaving(false);
  }

  const clientName = `${review.last_name || ''}, ${review.first_name || ''}`.trim().replace(/^,\s*/, '');

  return (
    <tr className="border-b border-gray-800 hover:bg-gray-800/40 transition-colors">
      <td className="px-3 py-3 text-sm text-white">{clientName || '—'}</td>
      <td className="px-3 py-3 text-xs text-gray-400">{review.carrier || '—'}</td>
      <td className="px-3 py-3">
        {editing ? (
          <select
            value={form.status}
            onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            className="bg-gray-800 text-white text-xs rounded px-2 py-1 border border-gray-700"
          >
            {['not_contacted','pending','scheduled','reviewed','overdue'].map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        ) : <StatusBadge status={review.status} />}
      </td>
      <td className="px-3 py-3">
        {editing ? (
          <select
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
            className="bg-gray-800 text-white text-xs rounded px-2 py-1 border border-gray-700"
          >
            {['CRITICAL','HIGH','STANDARD'].map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        ) : <PriorityBadge priority={review.priority} />}
      </td>
      <td className="px-3 py-3">
        {editing ? (
          <input
            type="checkbox"
            checked={form.soa_obtained}
            onChange={e => setForm(f => ({ ...f, soa_obtained: e.target.checked }))}
            className="accent-amber-400"
          />
        ) : (
          form.soa_obtained
            ? <Check size={14} className="text-emerald-400" />
            : <X size={14} className="text-gray-600" />
        )}
      </td>
      <td className="px-3 py-3 text-xs text-gray-400">
        {editing ? (
          <input
            type="datetime-local"
            value={form.appointment_date}
            onChange={e => setForm(f => ({ ...f, appointment_date: e.target.value }))}
            className="bg-gray-800 text-white text-xs rounded px-2 py-1 border border-gray-700"
          />
        ) : (
          review.appointment_date
            ? new Date(review.appointment_date).toLocaleDateString()
            : '—'
        )}
      </td>
      <td className="px-3 py-3 text-xs text-gray-400 max-w-xs truncate">
        {review.agent_name || '—'}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-1 text-emerald-400 hover:text-emerald-300"
              >
                <Save size={14} />
              </button>
              <button onClick={() => setEditing(false)} className="p-1 text-gray-500 hover:text-gray-300">
                <X size={14} />
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="p-1 text-gray-500 hover:text-amber-400">
              <Edit2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function CoverageReviewsTab({ year, orgId }) {
  const [reviews, setReviews]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatus]   = useState('');
  const [priorityFilter, setPriority] = useState('');
  const [sort, setSort]             = useState('priority');
  const [syncing, setSyncing]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (statusFilter)   params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/coverage-reviews?${params}`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setReviews(j.data || []);
    } catch (e) {
      console.error('coverage-reviews load error', e);
    } finally {
      setLoading(false);
    }
  }, [year, statusFilter, priorityFilter, sort]);

  useEffect(() => { load(); }, [load]);

  async function handleSync() {
    setSyncing(true);
    try {
      await fetch(`${BASE}/api/aep-readiness/${year}/coverage-reviews/sync-clients`, {
        method: 'POST', credentials: 'include',
      });
      await load();
    } finally {
      setSyncing(false);
    }
  }

  async function handleUpdate(id, form) {
    await fetch(`${BASE}/api/aep-readiness/${year}/coverage-reviews/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    await load();
  }

  const total    = reviews.length;
  const reviewed = reviews.filter(r => r.status === 'reviewed').length;
  const overdue  = reviews.filter(r => r.status === 'overdue').length;
  const soaMissing = reviews.filter(r => r.appointment_date && !r.soa_obtained).length;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Clients',   value: total,     color: 'text-gray-200' },
          { label: 'Reviewed',        value: reviewed,   color: 'text-emerald-400' },
          { label: 'Overdue',         value: overdue,    color: 'text-red-400' },
          { label: 'SOA Missing',     value: soaMissing, color: 'text-amber-400' },
        ].map(s => (
          <Card key={s.label} className="p-3">
            <p className="text-xs text-gray-400">{s.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`}>{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Overall progress */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-200">Overall Review Progress</span>
          <span className="text-sm font-bold tabular-nums text-amber-400">
            {reviewed}/{total} ({total > 0 ? Math.round((reviewed / total) * 100) : 0}%)
          </span>
        </div>
        <ProgressBar value={reviewed} max={Math.max(1, total)} color="bg-emerald-400" showPct={false} />
      </Card>

      {/* Filters + controls */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={statusFilter}
            onChange={e => setStatus(e.target.value)}
            className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
          >
            <option value="">All Statuses</option>
            {['not_contacted','pending','scheduled','reviewed','overdue'].map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={e => setPriority(e.target.value)}
            className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
          >
            <option value="">All Priorities</option>
            {['CRITICAL','HIGH','STANDARD'].map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
          >
            <option value="priority">Sort: Priority</option>
            <option value="status">Sort: Status</option>
            <option value="appointment_date">Sort: Appointment</option>
            <option value="agent">Sort: Agent</option>
            <option value="client">Sort: Client</option>
          </select>
          <div className="flex-1" />
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-gray-200 rounded transition-colors"
          >
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
            Sync Clients
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  {['Client','Carrier','Status','Priority','SOA','Appointment','Agent',''].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No reviews found. Click "Sync Clients" to import your clients.
                    </td>
                  </tr>
                ) : (
                  reviews.map(r => (
                    <ReviewRow key={r.id} review={r} onUpdate={handleUpdate} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// TAB 3: AGENT OPS
// ============================================================================
function AgentCertRow({ agent, year, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [carriers, setCarriers] = useState((agent.carriersCertified || []).join(', '));
  const [ahip, setAhip]         = useState(agent.ahipCompleted);
  const [saving, setSaving]     = useState(false);

  async function handleSave() {
    setSaving(true);
    await onUpdate(agent.agentId, {
      ahip_completed: ahip,
      carriers_certified: carriers.split(',').map(s => s.trim()).filter(Boolean),
    });
    setEditing(false);
    setSaving(false);
  }

  const utilColor = agent.utilizationPct >= 90 ? 'bg-red-400'
    : agent.utilizationPct >= 70 ? 'bg-amber-400' : 'bg-emerald-400';

  const BADGE_ICONS = {
    'early-bird':   <Flame size={12} />,
    'closer':       <Target size={12} />,
    'team-player':  <Users size={12} />,
    'compliance-ace': <Shield size={12} />,
    'top-enrollor': <Crown size={12} />,
  };

  return (
    <tr className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
      <td className="px-3 py-3">
        <div>
          <p className="text-sm text-white font-medium">{agent.agentName}</p>
          <p className="text-xs text-gray-500">{agent.agentEmail}</p>
        </div>
      </td>
      <td className="px-3 py-3">
        {editing ? (
          <input
            type="checkbox"
            checked={ahip}
            onChange={e => setAhip(e.target.checked)}
            className="accent-amber-400"
          />
        ) : (
          agent.ahipCompleted
            ? <Badge color="emerald"><CheckCircle2 size={12} /> Certified</Badge>
            : <Badge color="red"><AlertCircle size={12} /> Pending</Badge>
        )}
      </td>
      <td className="px-3 py-3 text-xs text-gray-400">
        {editing ? (
          <input
            value={carriers}
            onChange={e => setCarriers(e.target.value)}
            placeholder="Aetna, Cigna, Humana"
            className="bg-gray-800 text-white text-xs rounded px-2 py-1 border border-gray-700 w-full"
          />
        ) : (
          agent.carriersCertified?.length > 0
            ? agent.carriersCertified.join(', ')
            : <span className="text-gray-600">None logged</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="w-28">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">{agent.reviewsCompleted}/{agent.totalAssigned}</span>
            <span className="text-xs tabular-nums text-gray-300">{agent.utilizationPct}%</span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div className={`h-full ${utilColor} rounded-full transition-all`} style={{ width: `${agent.utilizationPct}%` }} />
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-sm font-bold tabular-nums text-amber-400">
        {fmt(agent.enrollments)}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1 flex-wrap">
          {agent.badges.map(b => (
            <span
              key={b.key}
              title={b.label}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-400/10 border border-amber-400/20 text-amber-400 rounded text-xs"
            >
              {BADGE_ICONS[b.key] || <Star size={12} />}
              {b.label}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving} className="p-1 text-emerald-400 hover:text-emerald-300">
                <Save size={14} />
              </button>
              <button onClick={() => setEditing(false)} className="p-1 text-gray-500 hover:text-gray-300">
                <X size={14} />
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="p-1 text-gray-500 hover:text-amber-400">
              <Edit2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AgentOpsTab({ year }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/agent-ops`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setData(j.data);
    } catch (e) {
      console.error('agent-ops load error', e);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  async function handleCertUpdate(agentId, payload) {
    await fetch(`${BASE}/api/aep-readiness/${year}/agent-certs/${agentId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    await load();
  }

  async function awardBadge(agentId, badgeKey, badgeLabel) {
    await fetch(`${BASE}/api/aep-readiness/${year}/badges`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_id: agentId, badge_key: badgeKey, badge_label: badgeLabel }),
    });
    await load();
  }

  if (loading) return <div className="text-center py-16 text-gray-500">Loading agent ops...</div>;
  if (!data)   return <div className="text-center py-16 text-gray-500">Failed to load.</div>;

  const { agents, recommendations } = data;
  const ahipDone = agents.filter(a => a.ahipCompleted).length;

  return (
    <div className="space-y-4">
      {/* Cert summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-3">
          <p className="text-xs text-gray-400">Total Agents</p>
          <p className="text-2xl font-bold tabular-nums text-gray-200 mt-1">{agents.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">AHIP Certified</p>
          <p className="text-2xl font-bold tabular-nums text-emerald-400 mt-1">{ahipDone}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">Pending Cert</p>
          <p className="text-2xl font-bold tabular-nums text-red-400 mt-1">{agents.length - ahipDone}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">Capacity Alerts</p>
          <p className="text-2xl font-bold tabular-nums text-amber-400 mt-1">
            {agents.filter(a => a.utilizationPct >= 90).length}
          </p>
        </Card>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card className="p-4">
          <SectionHeader title="Workload Recommendations" />
          <div className="space-y-2">
            {recommendations.map((r, i) => (
              <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                r.type === 'certification' ? 'bg-red-900/20 border border-red-500/30 text-red-300'
                  : 'bg-amber-900/20 border border-amber-500/30 text-amber-300'
              }`}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                {r.message}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Agent table */}
      <Card className="p-4">
        <SectionHeader title="Agent Certification & Capacity Grid" subtitle="Click edit to update AHIP status and certifications" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {['Agent','AHIP','Carriers Certified','Workload','Enrollments','Badges',''].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No agents found.</td></tr>
              ) : (
                agents.map(a => (
                  <AgentCertRow key={a.agentId} agent={a} year={year} onUpdate={handleCertUpdate} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick badge award */}
      {agents.length > 0 && (
        <Card className="p-4">
          <SectionHeader title="Award Gamification Badge" subtitle="Recognize top performers" />
          <QuickBadgeAwarder agents={agents} onAward={awardBadge} />
        </Card>
      )}
    </div>
  );
}

function QuickBadgeAwarder({ agents, onAward }) {
  const [agentId, setAgentId] = useState('');
  const [badgeKey, setBadgeKey] = useState('');
  const [awarding, setAwarding] = useState(false);

  const BADGES = [
    { key: 'early-bird',      label: 'Early Bird' },
    { key: 'closer',          label: 'Closer' },
    { key: 'team-player',     label: 'Team Player' },
    { key: 'compliance-ace',  label: 'Compliance Ace' },
    { key: 'top-enrollor',    label: 'Top Enroller' },
  ];

  async function handleAward() {
    if (!agentId || !badgeKey) return;
    setAwarding(true);
    const badge = BADGES.find(b => b.key === badgeKey);
    await onAward(agentId, badgeKey, badge?.label || badgeKey);
    setAwarding(false);
    setAgentId('');
    setBadgeKey('');
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <select
        value={agentId}
        onChange={e => setAgentId(e.target.value)}
        className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
      >
        <option value="">Select agent...</option>
        {agents.map(a => (
          <option key={a.agentId} value={a.agentId}>{a.agentName}</option>
        ))}
      </select>
      <select
        value={badgeKey}
        onChange={e => setBadgeKey(e.target.value)}
        className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
      >
        <option value="">Select badge...</option>
        {BADGES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
      </select>
      <button
        onClick={handleAward}
        disabled={!agentId || !badgeKey || awarding}
        className="flex items-center gap-1 px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-gray-900 text-sm font-medium rounded transition-colors disabled:opacity-40"
      >
        <Award size={14} /> Award Badge
      </button>
    </div>
  );
}

// ============================================================================
// TAB 4: CARRIERS
// ============================================================================
function CarriersTab({ year }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [anocForm, setAnocForm] = useState({ carrier: '', plan_name: '', change_type: '', change_description: '', premium_change: '', impact_level: 'LOW' });
  const [quotaForm, setQuotaForm] = useState({ carrier: '', target_enrollments: '', enrollment_cap: '' });
  const [showAnocForm, setShowAnocForm] = useState(false);
  const [showQuotaForm, setShowQuotaForm] = useState(false);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/carriers`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setData(j.data);
    } catch (e) {
      console.error('carriers load error', e);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  async function handleSaveQuota() {
    setSaving(true);
    await fetch(`${BASE}/api/aep-readiness/${year}/carriers/quota`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        carrier: quotaForm.carrier,
        target_enrollments: parseInt(quotaForm.target_enrollments) || 0,
        enrollment_cap: quotaForm.enrollment_cap ? parseInt(quotaForm.enrollment_cap) : null,
      }),
    });
    setShowQuotaForm(false);
    setQuotaForm({ carrier: '', target_enrollments: '', enrollment_cap: '' });
    await load();
    setSaving(false);
  }

  async function handleSaveAnoc() {
    setSaving(true);
    await fetch(`${BASE}/api/aep-readiness/${year}/carriers/anoc`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...anocForm,
        premium_change: anocForm.premium_change ? parseFloat(anocForm.premium_change) : null,
      }),
    });
    setShowAnocForm(false);
    setAnocForm({ carrier: '', plan_name: '', change_type: '', change_description: '', premium_change: '', impact_level: 'LOW' });
    await load();
    setSaving(false);
  }

  if (loading) return <div className="text-center py-16 text-gray-500">Loading carriers...</div>;

  const { carriers = [], anocChanges = [], commissionRates = [] } = data || {};

  return (
    <div className="space-y-4">
      {/* Carrier quotas */}
      <Card className="p-4">
        <SectionHeader
          title="Carrier Enrollment Quotas"
          subtitle="Track enrollments vs. target and cap per carrier"
          action={
            <button
              onClick={() => setShowQuotaForm(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-gray-200 rounded"
            >
              <Plus size={13} /> Add Quota
            </button>
          }
        />
        {showQuotaForm && (
          <div className="mb-4 p-3 bg-gray-800 rounded-lg flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Carrier</label>
              <input
                value={quotaForm.carrier}
                onChange={e => setQuotaForm(f => ({ ...f, carrier: e.target.value }))}
                placeholder="Aetna"
                className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-32"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Target</label>
              <input
                type="number"
                value={quotaForm.target_enrollments}
                onChange={e => setQuotaForm(f => ({ ...f, target_enrollments: e.target.value }))}
                placeholder="50"
                className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-20"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Cap (optional)</label>
              <input
                type="number"
                value={quotaForm.enrollment_cap}
                onChange={e => setQuotaForm(f => ({ ...f, enrollment_cap: e.target.value }))}
                placeholder="100"
                className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-20"
              />
            </div>
            <button
              onClick={handleSaveQuota}
              disabled={saving || !quotaForm.carrier}
              className="px-3 py-1.5 bg-amber-400 text-gray-900 text-sm font-medium rounded hover:bg-amber-300 disabled:opacity-40"
            >
              Save
            </button>
          </div>
        )}

        {carriers.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No carrier quotas set. Click "Add Quota" to get started.</p>
        ) : (
          <div className="space-y-4">
            {carriers.map(c => {
              const pct = c.targetEnrollments > 0
                ? Math.min(100, Math.round((c.actualEnrollments / c.targetEnrollments) * 100))
                : 0;
              const capPct = c.enrollmentCap
                ? Math.round((c.actualEnrollments / c.enrollmentCap) * 100)
                : null;
              const barColor = pct >= 100 ? 'bg-emerald-400' : pct >= 75 ? 'bg-amber-400' : 'bg-blue-400';

              return (
                <div key={c.carrier}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">{c.carrier}</span>
                    <div className="flex items-center gap-3 text-xs tabular-nums">
                      <span className="text-gray-400">{fmt(c.actualEnrollments)} / {fmt(c.targetEnrollments)} target</span>
                      {c.enrollmentCap && (
                        <span className={capPct >= 90 ? 'text-red-400' : 'text-gray-500'}>
                          (cap: {fmt(c.enrollmentCap)}, {capPct}% full)
                        </span>
                      )}
                      <span className={`font-bold ${pct >= 100 ? 'text-emerald-400' : 'text-gray-200'}`}>{pct}%</span>
                    </div>
                  </div>
                  <ProgressBar value={c.actualEnrollments} max={Math.max(1, c.targetEnrollments)} color={barColor} showPct={false} />
                  {c.enrollmentCap && (
                    <div className="mt-1">
                      <ProgressBar value={c.actualEnrollments} max={c.enrollmentCap} color="bg-gray-600" showPct={false} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ANOC changes */}
      <Card className="p-4">
        <SectionHeader
          title="ANOC Plan Changes"
          subtitle="Annual Notice of Change — document plan changes from carriers"
          action={
            <button
              onClick={() => setShowAnocForm(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-gray-200 rounded"
            >
              <Plus size={13} /> Log Change
            </button>
          }
        />
        {showAnocForm && (
          <div className="mb-4 p-3 bg-gray-800 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Carrier</label>
                <input
                  value={anocForm.carrier}
                  onChange={e => setAnocForm(f => ({ ...f, carrier: e.target.value }))}
                  placeholder="Humana"
                  className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Plan Name</label>
                <input
                  value={anocForm.plan_name}
                  onChange={e => setAnocForm(f => ({ ...f, plan_name: e.target.value }))}
                  placeholder="Gold Plus HMO"
                  className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Change Type</label>
                <select
                  value={anocForm.change_type}
                  onChange={e => setAnocForm(f => ({ ...f, change_type: e.target.value }))}
                  className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full"
                >
                  <option value="">Select...</option>
                  {['Premium Increase','Premium Decrease','Benefit Reduction','Benefit Enhancement','Formulary Change','Network Change','Plan Discontinuation'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Impact Level</label>
                <select
                  value={anocForm.impact_level}
                  onChange={e => setAnocForm(f => ({ ...f, impact_level: e.target.value }))}
                  className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full"
                >
                  {['LOW','MEDIUM','HIGH','CRITICAL'].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Premium Change ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={anocForm.premium_change}
                  onChange={e => setAnocForm(f => ({ ...f, premium_change: e.target.value }))}
                  placeholder="+15.50"
                  className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Description</label>
              <textarea
                value={anocForm.change_description}
                onChange={e => setAnocForm(f => ({ ...f, change_description: e.target.value }))}
                rows={2}
                placeholder="Describe the change..."
                className="bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 w-full resize-none"
              />
            </div>
            <button
              onClick={handleSaveAnoc}
              disabled={saving || !anocForm.carrier}
              className="px-3 py-1.5 bg-amber-400 text-gray-900 text-sm font-medium rounded hover:bg-amber-300 disabled:opacity-40"
            >
              Save ANOC Entry
            </button>
          </div>
        )}

        {anocChanges.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No ANOC changes logged yet.</p>
        ) : (
          <div className="space-y-2">
            {anocChanges.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                <div className="shrink-0 mt-0.5">
                  <Badge color={a.impact_level === 'CRITICAL' ? 'red' : a.impact_level === 'HIGH' ? 'amber' : 'gray'}>
                    {a.impact_level}
                  </Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white">{a.carrier}</span>
                    {a.plan_name && <span className="text-xs text-gray-400">— {a.plan_name}</span>}
                    {a.change_type && <Badge color="gray">{a.change_type}</Badge>}
                    {a.premium_change && (
                      <span className={`text-xs tabular-nums font-medium ${a.premium_change > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {a.premium_change > 0 ? '+' : ''}${a.premium_change}/mo
                      </span>
                    )}
                  </div>
                  {a.change_description && (
                    <p className="text-xs text-gray-400 mt-1">{a.change_description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Commission rates */}
      {commissionRates.length > 0 && (
        <Card className="p-4">
          <SectionHeader title="Commission Rates" subtitle="Used for post-AEP commission forecasting" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  {['Carrier','Plan Type','Initial Rate','Renewal Rate'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commissionRates.map((r, i) => (
                  <tr key={i} className="border-b border-gray-800">
                    <td className="px-3 py-2 text-white">{r.carrier}</td>
                    <td className="px-3 py-2 text-gray-400">{r.plan_type}</td>
                    <td className="px-3 py-2 tabular-nums text-emerald-400">{fmtMoney(r.initial_rate)}</td>
                    <td className="px-3 py-2 tabular-nums text-amber-400">{fmtMoney(r.renewal_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// TAB 5: POST-AEP
// ============================================================================
function PostAEPTab({ year }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateForm, setRateForm] = useState({ carrier: '', plan_type: 'MAPD', initial_rate: '', renewal_rate: '' });
  const [showRateForm, setShowRateForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/post-aep`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setData(j.data);
    } catch (e) {
      console.error('post-aep load error', e);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  async function handleSaveRate() {
    setSaving(true);
    await fetch(`${BASE}/api/aep-readiness/${year}/carriers/commission-rate`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        carrier: rateForm.carrier,
        plan_type: rateForm.plan_type,
        initial_rate: parseFloat(rateForm.initial_rate) || 0,
        renewal_rate: parseFloat(rateForm.renewal_rate) || 0,
      }),
    });
    setShowRateForm(false);
    setRateForm({ carrier: '', plan_type: 'MAPD', initial_rate: '', renewal_rate: '' });
    await load();
    setSaving(false);
  }

  if (loading) return <div className="text-center py-16 text-gray-500">Loading post-AEP data...</div>;
  if (!data)   return <div className="text-center py-16 text-gray-500">No post-AEP data.</div>;

  const { commissionForecast, churnMonitor, retrospective } = data;
  const { rows = [], totalProjectedAnnual = 0 } = commissionForecast || {};

  return (
    <div className="space-y-4">
      {/* Retrospective summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-3">
          <p className="text-xs text-gray-400">Total Enrollments</p>
          <p className="text-2xl font-bold tabular-nums text-amber-400 mt-1">{fmt(retrospective.totalEnrollments)}</p>
          {retrospective.enrollmentGoal > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">Goal: {fmt(retrospective.enrollmentGoal)} ({retrospective.goalPct}%)</p>
          )}
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">Projected Annual Revenue</p>
          <p className="text-2xl font-bold tabular-nums text-emerald-400 mt-1">{fmtMoney(totalProjectedAnnual)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">Total Clients</p>
          <p className="text-2xl font-bold tabular-nums text-gray-200 mt-1">{fmt(churnMonitor.totalClients)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-400">At-Risk Clients</p>
          <p className="text-2xl font-bold tabular-nums text-red-400 mt-1">{fmt(churnMonitor.atRisk)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Monitor for January disenrollment</p>
        </Card>
      </div>

      {/* Commission forecast */}
      <Card className="p-4">
        <SectionHeader
          title="Commission Forecast Engine"
          subtitle="Enrollment count × commission rate = projected revenue"
          action={
            <button
              onClick={() => setShowRateForm(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-gray-200 rounded"
            >
              <Plus size={13} /> Add Rate
            </button>
          }
        />
        {showRateForm && (
          <div className="mb-4 p-3 bg-gray-800 rounded-lg flex flex-wrap gap-3 items-end">
            {[
              { label: 'Carrier', key: 'carrier', type: 'text', placeholder: 'Aetna', width: 'w-28' },
              { label: 'Plan Type', key: 'plan_type', type: 'text', placeholder: 'MAPD', width: 'w-20' },
              { label: 'Initial Rate ($)', key: 'initial_rate', type: 'number', placeholder: '600', width: 'w-24' },
              { label: 'Renewal Rate ($)', key: 'renewal_rate', type: 'number', placeholder: '285', width: 'w-24' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                <input
                  type={f.type}
                  step={f.type === 'number' ? '0.01' : undefined}
                  value={rateForm[f.key]}
                  onChange={e => setRateForm(r => ({ ...r, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className={`bg-gray-900 text-white text-sm rounded px-2 py-1.5 border border-gray-700 ${f.width}`}
                />
              </div>
            ))}
            <button
              onClick={handleSaveRate}
              disabled={saving || !rateForm.carrier}
              className="px-3 py-1.5 bg-amber-400 text-gray-900 text-sm font-medium rounded hover:bg-amber-300 disabled:opacity-40"
            >
              Save
            </button>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            No forecast data. Add commission rates in Carriers tab, then log enrollments.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    {['Carrier','Plan Type','Enrollments','Initial Rate','Renewal Rate','1st Yr Revenue','Annual Revenue'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-gray-800">
                      <td className="px-3 py-2 text-white">{r.carrier || '—'}</td>
                      <td className="px-3 py-2 text-gray-400">{r.planType || 'MAPD'}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-200">{fmt(r.enrollments)}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-400">{fmtMoney(r.initialRate)}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-400">{fmtMoney(r.renewalRate)}</td>
                      <td className="px-3 py-2 tabular-nums text-amber-400">{fmtMoney(r.projectedFirst)}</td>
                      <td className="px-3 py-2 tabular-nums text-emerald-400 font-medium">{fmtMoney(r.projectedAnnual)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-700">
                    <td colSpan={5} className="px-3 py-2 text-sm font-bold text-gray-200">Total</td>
                    <td colSpan={2} className="px-3 py-2 text-sm font-bold tabular-nums text-emerald-400">
                      {fmtMoney(totalProjectedAnnual)} / year
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Churn monitor */}
      <Card className="p-4">
        <SectionHeader title="Churn Monitor" subtitle="January disenrollment tracking — check in early January" />
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-gray-800/50 rounded-lg text-center">
            <p className="text-xs text-gray-400 mb-1">Total Active Clients</p>
            <p className="text-3xl font-bold tabular-nums text-gray-200">{fmt(churnMonitor.totalClients)}</p>
          </div>
          <div className="p-3 bg-red-900/20 border border-red-500/20 rounded-lg text-center">
            <p className="text-xs text-gray-400 mb-1">At-Risk / Inactive</p>
            <p className="text-3xl font-bold tabular-nums text-red-400">{fmt(churnMonitor.atRisk)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {churnMonitor.totalClients > 0
                ? `${Math.round((churnMonitor.atRisk / churnMonitor.totalClients) * 100)}% of book`
                : '—'}
            </p>
          </div>
        </div>
        <div className="mt-3 p-3 bg-amber-900/10 border border-amber-400/20 rounded-lg text-xs text-amber-300">
          Tip: Reach out to at-risk clients within 30 days of Jan 1 to prevent plan lapses. Review the Retention Center for detailed churn risk scores.
        </div>
      </Card>

      {/* AEP Retrospective */}
      <Card className="p-4">
        <SectionHeader title={`AEP ${year} Retrospective`} subtitle="Summary of this season's performance" />
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Enrollment Goal Achievement</span>
            <span className="text-sm font-bold tabular-nums text-amber-400">{retrospective.goalPct}%</span>
          </div>
          <ProgressBar
            value={retrospective.totalEnrollments}
            max={Math.max(1, retrospective.enrollmentGoal)}
            color={retrospective.goalPct >= 100 ? 'bg-emerald-400' : retrospective.goalPct >= 75 ? 'bg-amber-400' : 'bg-red-400'}
            showPct={false}
          />
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[
              { label: 'Total Enrollments',          value: fmt(retrospective.totalEnrollments),       color: 'text-white' },
              { label: 'Enrollment Goal',             value: fmt(retrospective.enrollmentGoal),         color: 'text-gray-400' },
              { label: 'Projected Annual Commission', value: fmtMoney(retrospective.projectedAnnualRevenue), color: 'text-emerald-400' },
              { label: 'Goal Attainment',             value: fmtPct(retrospective.goalPct),             color: retrospective.goalPct >= 100 ? 'text-emerald-400' : 'text-amber-400' },
            ].map(s => (
              <div key={s.label} className="p-2 bg-gray-800/50 rounded">
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-lg font-bold tabular-nums mt-0.5 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
// GOAL CONFIGURATION MODAL
// ============================================================================
function GoalConfigModal({ year, current, onSave, onClose }) {
  const [form, setForm] = useState({
    enrollment_goal:      current?.enrollment_goal      || 0,
    coverage_review_goal: current?.coverage_review_goal || 0,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    await fetch(`${BASE}/api/aep-readiness/${year}/config`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    onSave();
    onClose();
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">AEP {year} Goals</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Enrollment Goal</label>
            <input
              type="number"
              value={form.enrollment_goal}
              onChange={e => setForm(f => ({ ...f, enrollment_goal: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Coverage Review Goal</label>
            <input
              type="number"
              value={form.coverage_review_goal}
              onChange={e => setForm(f => ({ ...f, coverage_review_goal: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-amber-400 text-gray-900 font-semibold rounded hover:bg-amber-300 disabled:opacity-40"
          >
            {saving ? 'Saving...' : 'Save Goals'}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// QUICK ENROLLMENT MODAL
// ============================================================================
function QuickEnrollModal({ year, onSave, onClose }) {
  const [form, setForm] = useState({ agent_id: '', carrier: '', plan_type: 'MAPD', effective_date: '' });
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/aep-readiness/${year}/agent-ops`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok) setAgents(j.data.agents || []); })
      .catch(() => {});
  }, [year]);

  async function handleSave() {
    if (!form.carrier) return;
    setSaving(true);
    await fetch(`${BASE}/api/aep-readiness/${year}/enrollments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    onSave();
    onClose();
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Log Enrollment</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Agent</label>
            <select
              value={form.agent_id}
              onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            >
              <option value="">Select agent...</option>
              {agents.map(a => <option key={a.agentId} value={a.agentId}>{a.agentName}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Carrier *</label>
            <input
              value={form.carrier}
              onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))}
              placeholder="Aetna"
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Plan Type</label>
            <select
              value={form.plan_type}
              onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            >
              {['MAPD','PDP','MA','Supplement'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Effective Date</label>
            <input
              type="date"
              value={form.effective_date}
              onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
              className="w-full bg-gray-800 text-white rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={saving || !form.carrier}
            className="flex-1 py-2 bg-amber-400 text-gray-900 font-semibold rounded hover:bg-amber-300 disabled:opacity-40"
          >
            {saving ? 'Logging...' : 'Log Enrollment'}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ROOT COMPONENT
// ============================================================================
const TABS = [
  { key: 'command',  label: 'Command Center',  icon: Activity },
  { key: 'reviews',  label: 'Coverage Reviews', icon: CheckCircle2 },
  { key: 'agents',   label: 'Agent Ops',        icon: Users },
  { key: 'carriers', label: 'Carriers',          icon: BarChart2 },
  { key: 'post',     label: 'Post-AEP',          icon: DollarSign },
];

export default function AEPReadiness() {
  const [activeTab, setActiveTab]   = useState('command');
  const [year, setYear]             = useState(() => {
    const now = new Date();
    const { phase, year: y } = getAepPhaseClient(now);
    return y;
  });
  const [ccData, setCcData]         = useState(null);
  const [ccLoading, setCcLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [config, setConfig]         = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  const loadCommandCenter = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/command-center`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setCcData(j.data);
    } catch (e) {
      console.error('command-center load error', e);
    } finally {
      setCcLoading(false);
      setRefreshing(false);
    }
  }, [year]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/api/aep-readiness/${year}/config`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setConfig(j.data);
    } catch (e) {
      console.error('config load error', e);
    }
  }, [year]);

  useEffect(() => {
    setCcLoading(true);
    loadCommandCenter();
    loadConfig();
  }, [loadCommandCenter, loadConfig]);

  function handleGoalSave() {
    loadCommandCenter();
    loadConfig();
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 lg:p-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">AEP Readiness</h1>
          <p className="text-sm text-gray-400 mt-0.5">Annual Enrollment Period Command Center</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Year selector */}
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            className="bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>AEP {y}</option>
            ))}
          </select>

          <button
            onClick={() => setShowGoalModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-gray-200 rounded transition-colors"
          >
            <Target size={13} /> Set Goals
          </button>

          <button
            onClick={() => setShowEnrollModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-gray-900 text-sm font-medium rounded transition-colors"
          >
            <Plus size={13} /> Log Enrollment
          </button>

          <button
            onClick={loadCommandCenter}
            disabled={refreshing}
            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* AEP Status Banner */}
      <AEPStatusBanner year={year} />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-800 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'text-amber-400 border-amber-400'
                  : 'text-gray-400 border-transparent hover:text-gray-200'
              }`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'command' && (
        ccLoading ? (
          <div className="text-center py-16 text-gray-500">Loading command center...</div>
        ) : ccData ? (
          <CommandCenterTab
            data={ccData}
            year={year}
            onRefresh={loadCommandCenter}
            refreshing={refreshing}
          />
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 mb-4">Could not load command center data.</p>
            <button
              onClick={loadCommandCenter}
              className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
            >
              Retry
            </button>
          </div>
        )
      )}

      {activeTab === 'reviews'  && <CoverageReviewsTab year={year} />}
      {activeTab === 'agents'   && <AgentOpsTab year={year} />}
      {activeTab === 'carriers' && <CarriersTab year={year} />}
      {activeTab === 'post'     && <PostAEPTab year={year} />}

      {/* Modals */}
      {showGoalModal && (
        <GoalConfigModal
          year={year}
          current={config}
          onSave={handleGoalSave}
          onClose={() => setShowGoalModal(false)}
        />
      )}
      {showEnrollModal && (
        <QuickEnrollModal
          year={year}
          onSave={loadCommandCenter}
          onClose={() => setShowEnrollModal(false)}
        />
      )}
    </div>
  );
}
